/**
 * The editor bar's save state: whether the reader's latest work has landed,
 * or why nothing here can change. Quiet metadata text, not a chip, so it
 * never reads as a button.
 *
 * @module components/editor/SaveStatus
 */

import { FloatingTip, Icon, IconButton, Spinner } from "@/components/ui";
import type { EditorProgrammeSyncValue } from "@/platform";
import { formatDateTime } from "@/utils/format-instant";

/** Save status display. */
export interface SaveStatusProps {
  lastSaved: string | null;
  backendSync: EditorProgrammeSyncValue | null;
  /** Why the content is closed for editing, which replaces the save state. */
  readOnly?: { short: string; full: string } | null;
}

export function SaveStatus({ lastSaved, backendSync, readOnly = null }: SaveStatusProps) {
  let stateName = "idle";
  let icon = "cloud";
  let tone = "text-muted";
  let statusText: string;
  if (lastSaved) {
    statusText = "All changes saved";
  } else {
    statusText = "Changes save automatically";
  }
  let statusTitle = lastSaved
    ? `Last saved ${formatDateTime(lastSaved)}`
    : "Changes are saved automatically as you work.";

  if (backendSync?.status === "loading") {
    statusText = "Loading programme...";
    stateName = "loading";
    icon = "spinner-gap";
  } else if (backendSync?.status === "saved-locally") {
    statusText = "Changes waiting to save";
    statusTitle = "Your latest changes have not yet been confirmed by the platform.";
    stateName = "pending";
    icon = "cloud-arrow-up";
    tone = "text-warning-strong";
  } else if (backendSync?.status === "saving-backend") {
    statusText = "Autosaving...";
    statusTitle = "Saving your latest changes to the platform.";
    stateName = "saving";
    icon = "spinner-gap";
    tone = "text-muted";
  } else if (readOnly || backendSync?.status === "read-only") {
    statusText = "Read-only";
    statusTitle = "This programme is open for viewing and cannot be changed.";
    stateName = "read-only";
    icon = "eye";
    tone = "text-muted";
  } else if (backendSync?.status === "saved-backend") {
    // The status alone, not the record: a save that has landed is reported
    // whether or not a record is loaded beside it. Requiring both said
    // "idle" on a programme that had just been written to the platform.
    statusText = "All changes saved";
    statusTitle = backendSync.record
      ? `Saved to the platform at revision ${backendSync.record.saveRevision}.`
      : "Saved to the platform.";
    stateName = "saved";
    icon = "cloud-check";
    tone = "text-muted";
  } else if (backendSync?.status === "locked") {
    statusText = "Editing locked";
    statusTitle = "Another user currently holds the programme editing lock.";
    stateName = "locked";
    icon = "lock-key";
    tone = "text-warning-strong";
  } else if (backendSync?.status === "conflict") {
    statusText = "Changes not saved";
    statusTitle = "The programme changed elsewhere. Review the conflict before reloading.";
    stateName = "conflict";
    icon = "warning-circle";
    tone = "text-error-strong";
  } else if (backendSync?.status === "unauthorized") {
    statusText = "Cannot save changes";
    statusTitle = "Your account does not have permission to save this programme.";
    stateName = "unauthorized";
    icon = "warning-circle";
    tone = "text-error-strong";
  } else if (backendSync?.status === "error") {
    statusText = "Save failed";
    statusTitle = "The platform could not save your latest changes.";
    stateName = "error";
    icon = "warning-circle";
    tone = "text-error-strong";
  } else if (lastSaved) {
    stateName = "saved";
    icon = "cloud-check";
    tone = "text-muted";
  }

  if (readOnly && stateName === "read-only") {
    return (
      <span
        id="saveStatus"
        className={`inline-flex items-center gap-1.5 text-md ${tone}`}
        data-testid="save-status"
        data-save-state={stateName}
        role="status"
      >
        <Icon name={icon} aria-hidden />
        {/* Hovering the text or focusing the button opens the full reason. */}
        <FloatingTip tip={readOnly.full}>
          {({ "aria-describedby": describedBy, ...trigger }) => (
            <span {...trigger} className="inline-flex items-center gap-1">
              {readOnly.short}
              <IconButton
                icon="info"
                size="xs"
                aria-label="Why it is read-only"
                aria-describedby={describedBy}
                data-testid="read-only-reason"
              />
            </span>
          )}
        </FloatingTip>
      </span>
    );
  }

  return (
    <span
      id="saveStatus"
      className={`inline-flex items-center gap-1.5 text-md ${tone}`}
      data-testid="save-status"
      data-save-state={stateName}
      role="status"
      aria-live="polite"
      title={statusTitle}
    >
      {stateName === "saving" || stateName === "loading" ? (
        <Spinner size="xs" />
      ) : (
        <Icon name={icon} aria-hidden />
      )}
      {statusText}
    </span>
  );
}
